import * as React from "react";
import { useState, useEffect } from "react";
import { Dialog } from "@/components/ui";
import { MfaMethod } from "@/services/bedrock/dtos";
import { ShieldAlert } from "lucide-react";
import { MfaChallengeContext, Step } from "./context";
import { SelectMethod } from "./components/SelectMethod";
import { EnterCode } from "./components/EnterCode";
import { VerifyingPasskey } from "./components/VerifyingPasskey";
import { PromptSetup } from "./components/PromptSetup";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  onCancel: () => void;
}

export const MfaChallengeDialog: React.FC<Props> = ({ open, onOpenChange, onSuccess, onCancel }) => {
  const [step, setStep] = useState<Step>("SELECT_METHOD");
  const [method, setMethod] = useState<MfaMethod | null>(null);
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync state when dialog opens
  useEffect(() => {
    if (open) {
      setStep("SELECT_METHOD");
      setMethod(null);
      setCode("");
      setIsLoading(false);
      setError(null);
    }
  }, [open]);

  const contextValue = {
    step,
    setStep,
    method,
    setMethod,
    code,
    setCode,
    isLoading,
    setIsLoading,
    error,
    setError,
    onOpenChange,
    onSuccess,
    onCancel,
  };

  return (
    <MfaChallengeContext.Provider value={contextValue}>
      <Dialog
        open={open}
        onOpenChange={val => {
          if (!val) onCancel();
          onOpenChange(val);
        }}
        size="md"
      >
        <Dialog.Header className="flex flex-row items-center p-6">
          <div className="h-12 w-12 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <Dialog.Title className="text-2xl font-bold text-slate-800 ml-2">Verify Your Identity</Dialog.Title>
        </Dialog.Header>
        <Dialog.Content className="p-6 gap-0">
          <div className="flex-1 overflow-y-auto">
            {step === "SELECT_METHOD" && <SelectMethod />}
            {step === "ENTER_CODE" && <EnterCode />}
            {step === "VERIFYING_PASSKEY" && <VerifyingPasskey />}
            {step === "PROMPT_SETUP" && <PromptSetup />}
          </div>
        </Dialog.Content>
      </Dialog>
    </MfaChallengeContext.Provider>
  );
};
